/**
 * ⚠️ CRITICAL STANDARDS COMPLIANCE ⚠️
 *
 * This component MUST use Material Design 3 web components from @material/web.
 *
 * ✅ ALLOWED:
 * - <md-filled-button>, <md-outlined-button>, <md-text-button>
 * - <md-outlined-text-field>, <md-filled-text-field>
 * - <md-filled-select>, <md-outlined-select> with <md-select-option>
 * - <md-checkbox>
 *
 * ❌ FORBIDDEN:
 * - Native <button>, <input>, <select>, <textarea>
 *
 * See:
 * - /guides/STANDARDS_STYLES.md#material-design-3
 * - /packages/ui-lit/src/df-upload-link.ts (compliant example)
 *
 * Build will FAIL if native HTML elements detected.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { deleteFile } from '@df/state';
/**
 * Delete confirmation component for files from Firebase Storage
 *
 * Provides a confirmation dialog before deleting files.
 *
 * @fires delete-confirm - Fired when user confirms deletion
 * @fires delete-cancel - Fired when user cancels deletion
 * @fires delete-complete - Fired when deletion completes successfully
 * @fires delete-error - Fired when deletion fails
 *
 * @example
 * ```html
 * <df-file-delete
 *   .file=${fileMetadata}
 *   @delete-complete=${() => refreshFileList()}
 * ></df-file-delete>
 * ```
 */
let DfFileDelete = class DfFileDelete extends LitElement {
    constructor() {
        super();
        this.file = null;
        this.showButton = true;
        this.buttonLabel = 'Delete';
        this.dialogOpen = false;
        this.deleting = false;
        this.error = null;
    }
    static { this.styles = css `
    :host {
      display: inline-block;
      --md-sys-color-primary: #5f9ea0;
    }

    .delete-button {
      --md-text-button-label-text-color: var(--md-sys-color-error, #b00020);
    }

    .dialog-content {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 24px;
      min-width: 300px;
    }

    .dialog-header {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .dialog-header md-icon {
      font-size: 32px;
      color: var(--md-sys-color-error, #b00020);
    }

    .dialog-title {
      font-size: 1.25rem;
      font-weight: 500;
    }

    .file-info {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px;
      background-color: var(--md-sys-color-surface-variant, #f5f5f5);
      border-radius: 8px;
    }

    .file-icon {
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      background-color: var(--md-sys-color-error-container, #fdecea);
    }

    .file-icon md-icon {
      color: var(--md-sys-color-error, #b00020);
    }

    .file-details {
      flex: 1;
      min-width: 0;
    }

    .file-name {
      font-weight: 500;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .file-path {
      font-size: 0.875rem;
      color: var(--md-sys-color-on-surface-variant, #666);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .warning-message {
      color: var(--md-sys-color-error, #b00020);
      font-size: 0.875rem;
      padding: 12px;
      background-color: var(--md-sys-color-error-container, #fdecea);
      border-radius: 8px;
    }

    .error-message {
      color: var(--md-sys-color-error, #b00020);
      padding: 12px;
      background-color: var(--md-sys-color-error-container, #fdecea);
      border-radius: 8px;
      font-size: 0.875rem;
    }

    .dialog-actions {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      padding-top: 8px;
    }

    md-filled-button.delete-action {
      --md-filled-button-container-color: var(--md-sys-color-error, #b00020);
    }
  `; }
    /**
     * Open the delete confirmation dialog
     */
    open() {
        this.dialogOpen = true;
        this.error = null;
    }
    /**
     * Close the dialog
     */
    close() {
        this.dialogOpen = false;
        this.error = null;
    }
    handleButtonClick() {
        this.open();
    }
    handleCancel() {
        this.close();
        this.dispatchEvent(new CustomEvent('delete-cancel', {
            bubbles: true,
            composed: true,
        }));
    }
    async handleConfirm() {
        if (!this.file)
            return;
        this.dispatchEvent(new CustomEvent('delete-confirm', {
            detail: { file: this.file },
            bubbles: true,
            composed: true,
        }));
        this.deleting = true;
        this.error = null;
        try {
            await deleteFile(this.file.path);
            this.dispatchEvent(new CustomEvent('delete-complete', {
                detail: { file: this.file },
                bubbles: true,
                composed: true,
            }));
            this.close();
        }
        catch (error) {
            this.error = error instanceof Error ? error.message : 'Failed to delete file';
            this.dispatchEvent(new CustomEvent('delete-error', {
                detail: {
                    file: this.file,
                    error: this.error,
                },
                bubbles: true,
                composed: true,
            }));
        }
        finally {
            this.deleting = false;
        }
    }
    getFileIcon(contentType) {
        if (contentType.startsWith('image/'))
            return 'image';
        if (contentType.startsWith('video/'))
            return 'movie';
        if (contentType.startsWith('audio/'))
            return 'audio_file';
        if (contentType.includes('pdf'))
            return 'picture_as_pdf';
        if (contentType.includes('zip') || contentType.includes('archive'))
            return 'folder_zip';
        return 'insert_drive_file';
    }
    render() {
        return html `
      ${this.showButton
            ? html `
            <md-text-button class="delete-button" @click=${this.handleButtonClick}>
              <md-icon slot="icon">delete</md-icon>
              ${this.buttonLabel}
            </md-text-button>
          `
            : ''}
      ${this.dialogOpen ? this.renderDialog() : ''}
    `;
    }
    renderDialog() {
        if (!this.file) {
            return html `
        <div class="dialog-content">
          <div class="error-message">No file selected for deletion</div>
          <div class="dialog-actions">
            <md-text-button @click=${this.handleCancel}>Close</md-text-button>
          </div>
        </div>
      `;
        }
        return html `
      <div class="dialog-content">
        <div class="dialog-header">
          <md-icon>warning</md-icon>
          <div class="dialog-title">Delete File?</div>
        </div>

        <div class="file-info">
          <div class="file-icon">
            <md-icon>${this.getFileIcon(this.file.contentType)}</md-icon>
          </div>
          <div class="file-details">
            <div class="file-name">${this.file.name}</div>
            <div class="file-path">${this.file.path}</div>
          </div>
        </div>

        <div class="warning-message">
          <strong>Warning:</strong> This action cannot be undone. The file will be permanently
          deleted from storage.
        </div>

        ${this.error
            ? html ` <div class="error-message"><strong>Error:</strong> ${this.error}</div> `
            : ''}

        <div class="dialog-actions">
          <md-text-button @click=${this.handleCancel} ?disabled=${this.deleting}>
            Cancel
          </md-text-button>
          <md-filled-button
            class="delete-action"
            @click=${this.handleConfirm}
            ?disabled=${this.deleting}
          >
            <md-icon slot="icon">${this.deleting ? 'hourglass_empty' : 'delete'}</md-icon>
            ${this.deleting ? 'Deleting...' : 'Delete'}
          </md-filled-button>
        </div>
      </div>
    `;
    }
};
__decorate([
    property({ type: Object })
], DfFileDelete.prototype, "file", void 0);
__decorate([
    property({ type: Boolean })
], DfFileDelete.prototype, "showButton", void 0);
__decorate([
    property({ type: String })
], DfFileDelete.prototype, "buttonLabel", void 0);
__decorate([
    state()
], DfFileDelete.prototype, "dialogOpen", void 0);
__decorate([
    state()
], DfFileDelete.prototype, "deleting", void 0);
__decorate([
    state()
], DfFileDelete.prototype, "error", void 0);
DfFileDelete = __decorate([
    customElement('df-file-delete')
], DfFileDelete);
export { DfFileDelete };
//# sourceMappingURL=df-file-delete.js.map